import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Users, UserPlus, Search, Loader2, Trash2, X, Plus, Mail, Check,
  RefreshCw, MoveRight, Archive, AlertCircle, Inbox, ChevronDown,
} from 'lucide-react';

const API = '/api'; // Same-origin: /api routes on the serving domain (Preview or Production custom domain) to backend via Emergent ingress. Avoids cross-domain CORS / tracking-prevention failures.

const STATUS_COLORS = {
  active: 'bg-hg-green/10 text-hg-green border-hg-green/30',
  unsubscribed: 'bg-red-100 text-red-700 border-red-200',
  unconfirmed: 'bg-amber-100 text-amber-700 border-amber-200',
  bounced: 'bg-orange-100 text-orange-700 border-orange-200',
  junk: 'bg-zinc-200 text-zinc-700 border-zinc-300',
};

function Toast({ message, type = 'success' }) {
  if (!message) return null;
  const color = type === 'error' ? 'border-red-300 text-red-700 bg-red-50' : 'border-hg-green/30 text-hg-green bg-white';
  return (
    <div className={`fixed top-4 right-4 z-50 border rounded-sm px-4 py-3 text-sm font-sans flex items-center gap-2 shadow-md ${color}`}>
      <Check size={14} /> {message}
    </div>
  );
}

function AddPersonModal({ groups, defaultGroupId, onClose, onAdded }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [selectedGroups, setSelectedGroups] = useState(defaultGroupId ? [defaultGroupId] : []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [duplicate, setDuplicate] = useState(null);  // { id, email, name, groups }
  const [checkingDup, setCheckingDup] = useState(false);

  const toggleGroup = (id) => {
    setSelectedGroups((s) => s.includes(id) ? s.filter((g) => g !== id) : [...s, id]);
  };

  // Auto-check for duplicates when email is valid + on blur
  const checkDuplicate = async () => {
    const em = email.trim().toLowerCase();
    if (!em.includes('@')) { setDuplicate(null); return; }
    setCheckingDup(true);
    try {
      const r = await axios.get(`${API}/people/lookup`, { params: { email: em } });
      if (r.data.exists) setDuplicate(r.data.subscriber); else setDuplicate(null);
    } catch (e) { setDuplicate(null); }
    setCheckingDup(false);
  };

  const submit = async () => {
    setErr('');
    if (!email.trim()) return setErr('Email required');
    setBusy(true);
    try {
      if (duplicate) {
        // Add to selected groups + optionally update name
        await axios.put(`${API}/people/${duplicate.id}`, {
          name: name.trim() || duplicate.name || undefined,
          add_group_ids: selectedGroups.filter((g) => !duplicate.groups.some((eg) => eg.id === g)),
          status: 'active',  // reactivate if previously unsubscribed
        });
      } else {
        await axios.post(`${API}/people`, {
          email: email.trim(),
          name: name.trim(),
          group_ids: selectedGroups,
        });
      }
      onAdded();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to add');
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-hg-border rounded-sm w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="add-person-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg text-hg-text tracking-wider">ADD PERSON</h3>
          <button onClick={onClose} className="text-hg-text-dim hover:text-hg-text"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">Email</label>
            <input
              data-testid="add-person-email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setDuplicate(null); }}
              onBlur={checkDuplicate}
              type="email"
              placeholder="name@example.com"
              className="w-full bg-white border border-hg-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-hg-green"
            />
            {checkingDup && <div className="text-[10px] text-hg-text-dim">Checking for duplicates…</div>}
            {duplicate && (
              <div className="bg-amber-50 border border-amber-300 rounded-sm p-2.5 text-xs space-y-1" data-testid="duplicate-warning">
                <div className="flex items-center gap-1.5 font-medium text-amber-800">
                  <AlertCircle size={12} /> Already in Mailerlite
                </div>
                <div className="text-amber-700">
                  <strong>{duplicate.name || '(no name)'}</strong> · status: {duplicate.status}
                </div>
                {duplicate.groups.length > 0 && (
                  <div className="text-[10px] text-amber-700">In groups: {duplicate.groups.map((g) => g.name).join(', ')}</div>
                )}
                <div className="text-[10px] text-amber-800 mt-1">Submitting will add them to selected groups (no duplicate created).</div>
              </div>
            )}
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">Name (optional)</label>
            <input
              data-testid="add-person-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={duplicate?.name || "Full name"}
              className="w-full bg-white border border-hg-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-hg-green"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">Groups</label>
            <div className="border border-hg-border rounded-sm max-h-44 overflow-y-auto p-1">
              {groups.map((g) => {
                const alreadyIn = duplicate?.groups.some((eg) => eg.id === g.id);
                return (
                  <label key={g.id} className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-hg-surface-hover rounded-sm">
                    <input
                      type="checkbox"
                      checked={selectedGroups.includes(g.id) || alreadyIn}
                      disabled={alreadyIn}
                      onChange={() => toggleGroup(g.id)}
                      className="accent-hg-green"
                      data-testid={`add-person-group-${g.id}`}
                    />
                    <span className="text-xs font-sans flex-1 truncate">{g.name}</span>
                    {alreadyIn && <span className="text-[9px] text-amber-700">already in</span>}
                    <span className="text-[10px] text-hg-text-dim">{g.active_count}</span>
                  </label>
                );
              })}
            </div>
          </div>
          {err && <div className="text-xs text-red-600 font-sans">{err}</div>}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-2 text-xs border border-hg-border rounded-sm">Cancel</button>
          <button
            data-testid="add-person-submit"
            onClick={submit}
            disabled={busy}
            className="px-3 py-2 text-xs bg-hg-green text-white rounded-sm hover:bg-hg-green-hover disabled:opacity-40 flex items-center gap-1.5"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            {duplicate ? 'Update + Add to Groups' : 'Add Person'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateGroupModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/people/groups`, { name: name.trim() });
      onCreated();
      onClose();
    } catch (e) { /* */ }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-hg-border rounded-sm w-full max-w-sm p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-lg text-hg-text tracking-wider">NEW GROUP</h3>
        <input
          data-testid="new-group-name"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Group name (e.g. NA Fitters - West)"
          className="w-full bg-white border border-hg-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-hg-green"
        />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-xs border border-hg-border rounded-sm">Cancel</button>
          <button data-testid="new-group-create" onClick={submit} disabled={busy || !name.trim()} className="px-3 py-2 text-xs bg-hg-green text-white rounded-sm disabled:opacity-40">
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MoveModal({ subscribers, groups, fromGroupId, onClose, onMoved }) {
  const [toGroupId, setToGroupId] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!toGroupId) return;
    setBusy(true);
    try {
      await axios.post(`${API}/people/move`, {
        subscriber_ids: subscribers.map((s) => s.id),
        from_group_id: fromGroupId || null,
        to_group_id: toGroupId,
      });
      onMoved();
      onClose();
    } catch (e) { /* */ }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-hg-border rounded-sm w-full max-w-sm p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-lg text-hg-text tracking-wider">MOVE {subscribers.length} {subscribers.length === 1 ? 'PERSON' : 'PEOPLE'}</h3>
        <select
          data-testid="move-target-group"
          value={toGroupId}
          onChange={(e) => setToGroupId(e.target.value)}
          className="w-full bg-white border border-hg-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-hg-green"
        >
          <option value="">— Choose target group —</option>
          {groups.filter((g) => g.id !== fromGroupId).map((g) => (
            <option key={g.id} value={g.id}>{g.name} ({g.active_count})</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={!!fromGroupId} disabled className="accent-hg-green" />
          <span className="text-hg-text-dim">{fromGroupId ? 'Also remove from current group' : 'Will just be added to target'}</span>
        </label>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-xs border border-hg-border rounded-sm">Cancel</button>
          <button data-testid="move-submit" onClick={submit} disabled={busy || !toGroupId} className="px-3 py-2 text-xs bg-hg-green text-white rounded-sm disabled:opacity-40">
            {busy ? 'Moving…' : 'Move'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PeopleHub() {
  const [groups, setGroups] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [people, setPeople] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [cursor, setCursor] = useState(null);
  const [cursorStack, setCursorStack] = useState([]);  // history for going back
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [toast, setToast] = useState({ msg: '', type: 'success' });
  const [showAdd, setShowAdd] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [showMove, setShowMove] = useState(false);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'success' }), 4000);
  };

  const loadGroups = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/people/groups`);
      setGroups(r.data.groups || []);
    } catch (e) { /* */ }
  }, []);

  const loadPeople = useCallback(async () => {
    setLoading(true);
    try {
      const params = { per_page: 50 };
      if (activeGroupId) params.group_id = activeGroupId;
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      if (cursor) params.cursor = cursor;
      const r = await axios.get(`${API}/people`, { params });
      setPeople(r.data.items || []);
      setNextCursor(r.data.next_cursor || null);
      setSelected(new Set());
    } catch (e) {
      showToast(e.response?.data?.detail || 'Failed to load people', 'error');
    }
    setLoading(false);
  }, [activeGroupId, statusFilter, search, cursor]);

  useEffect(() => { loadGroups(); }, [loadGroups]);
  useEffect(() => { loadPeople(); }, [loadPeople]);

  const toggleSelect = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const selectAll = () => {
    if (selected.size === people.length) setSelected(new Set());
    else setSelected(new Set(people.map((p) => p.id)));
  };

  const archiveSelected = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Archive ${selected.size} ${selected.size === 1 ? 'person' : 'people'}? They will no longer receive emails.`)) return;
    try {
      for (const id of selected) {
        await axios.put(`${API}/people/${id}`, { status: 'unsubscribed' });
      }
      showToast(`Archived ${selected.size}`);
      await loadPeople(); await loadGroups();
    } catch (e) {
      showToast(e.response?.data?.detail || 'Archive failed', 'error');
    }
  };

  const removeFromGroup = async () => {
    if (selected.size === 0 || !activeGroupId) return;
    if (!confirm(`Remove ${selected.size} from this group? They stay in Mailerlite but leave this list.`)) return;
    try {
      for (const id of selected) {
        await axios.put(`${API}/people/${id}`, { remove_group_ids: [activeGroupId] });
      }
      showToast(`Removed ${selected.size} from group`);
      await loadPeople(); await loadGroups();
    } catch (e) { showToast(e.response?.data?.detail || 'Remove failed', 'error'); }
  };

  const deleteForever = async () => {
    if (selected.size === 0) return;
    if (!confirm(`PERMANENTLY DELETE ${selected.size} from Mailerlite? This cannot be undone.`)) return;
    try {
      for (const id of selected) {
        await axios.delete(`${API}/people/${id}`);
      }
      showToast(`Deleted ${selected.size}`);
      await loadPeople(); await loadGroups();
    } catch (e) { showToast(e.response?.data?.detail || 'Delete failed', 'error'); }
  };

  const selectedPeople = people.filter((p) => selected.has(p.id));

  return (
    <div className="space-y-5" data-testid="people-hub">
      <Toast {...toast} />
      {showAdd && <AddPersonModal groups={groups} defaultGroupId={activeGroupId} onClose={() => setShowAdd(false)} onAdded={() => { loadPeople(); loadGroups(); showToast('Person added'); }} />}
      {showNewGroup && <CreateGroupModal onClose={() => setShowNewGroup(false)} onCreated={() => { loadGroups(); showToast('Group created'); }} />}
      {showMove && <MoveModal subscribers={selectedPeople} groups={groups} fromGroupId={activeGroupId} onClose={() => setShowMove(false)} onMoved={() => { loadPeople(); loadGroups(); showToast('Moved'); }} />}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Users size={14} className="text-hg-green" strokeWidth={1.5} />
            <h2 className="font-display text-xl tracking-wider">PEOPLE</h2>
          </div>
          <p className="text-xs text-hg-text-dim font-sans mt-1">Live Mailerlite contacts. Add, move, archive, or delete — changes go straight to your sending lists.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="people-new-group" onClick={() => setShowNewGroup(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-hg-border rounded-sm hover:border-hg-green hover:text-hg-green">
            <Plus size={12} /> New Group
          </button>
          <button data-testid="people-add-person" onClick={() => setShowAdd(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-hg-green text-white rounded-sm hover:bg-hg-green-hover">
            <UserPlus size={12} /> Add Person
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        {/* Sidebar: groups */}
        <aside className="space-y-2">
          <div className="bg-white border border-hg-border rounded-sm p-3 space-y-1">
            <div className="text-[10px] font-sans text-hg-text-dim uppercase tracking-[0.2em] px-2 pb-1">Groups</div>
            <button
              data-testid="people-group-all"
              onClick={() => { setActiveGroupId(null); setCursor(null); setCursorStack([]); }}
              className={`w-full text-left px-2 py-1.5 text-xs font-sans rounded-sm flex items-center justify-between ${!activeGroupId ? 'bg-hg-green/10 text-hg-green' : 'text-hg-text-dim hover:bg-hg-surface-hover'}`}
            >
              <span className="flex items-center gap-1.5"><Inbox size={11} /> All contacts</span>
            </button>
            {groups.map((g) => (
              <button
                key={g.id}
                data-testid={`people-group-${g.id}`}
                onClick={() => { setActiveGroupId(g.id); setCursor(null); setCursorStack([]); }}
                className={`w-full text-left px-2 py-1.5 text-xs font-sans rounded-sm flex items-center justify-between gap-2 ${activeGroupId === g.id ? 'bg-hg-green/10 text-hg-green' : 'text-hg-text-dim hover:bg-hg-surface-hover'}`}
              >
                <span className="truncate">{g.name}</span>
                <span className="text-[10px] opacity-70 flex-shrink-0">{g.active_count}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* Main */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-hg-text-dim" />
              <input
                data-testid="people-search"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCursor(null); setCursorStack([]); }}
                placeholder="Search email or name…"
                className="w-full bg-white border border-hg-border rounded-sm pl-8 pr-3 py-2 text-xs focus:outline-none focus:border-hg-green"
              />
            </div>
            <select
              data-testid="people-status-filter"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCursor(null); setCursorStack([]); }}
              className="bg-white border border-hg-border rounded-sm px-3 py-2 text-xs focus:outline-none focus:border-hg-green"
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="unsubscribed">Unsubscribed</option>
              <option value="unconfirmed">Unconfirmed</option>
              <option value="bounced">Bounced</option>
              <option value="junk">Junk</option>
            </select>
            <button onClick={loadPeople} className="px-3 py-2 text-xs border border-hg-border rounded-sm hover:border-hg-green hover:text-hg-green" data-testid="people-refresh">
              <RefreshCw size={12} />
            </button>
          </div>

          {/* Action bar */}
          {selected.size > 0 && (
            <div className="flex items-center gap-2 bg-hg-green/10 border border-hg-green/30 rounded-sm px-3 py-2 text-xs font-sans" data-testid="people-action-bar">
              <span className="text-hg-green font-medium">{selected.size} selected</span>
              <div className="flex-1" />
              <button data-testid="people-action-move" onClick={() => setShowMove(true)} className="flex items-center gap-1 px-2 py-1 hover:bg-white rounded-sm"><MoveRight size={11} /> Move</button>
              {activeGroupId && (
                <button data-testid="people-action-remove" onClick={removeFromGroup} className="flex items-center gap-1 px-2 py-1 hover:bg-white rounded-sm"><X size={11} /> Remove from group</button>
              )}
              <button data-testid="people-action-archive" onClick={archiveSelected} className="flex items-center gap-1 px-2 py-1 hover:bg-white rounded-sm"><Archive size={11} /> Archive</button>
              <button data-testid="people-action-delete" onClick={deleteForever} className="flex items-center gap-1 px-2 py-1 text-red-600 hover:bg-red-50 rounded-sm"><Trash2 size={11} /> Delete</button>
            </div>
          )}

          {/* Table */}
          <div className="bg-white border border-hg-border rounded-sm overflow-hidden">
            <div className="px-4 py-2 border-b border-hg-border flex items-center gap-3 text-[10px] font-sans uppercase tracking-[0.2em] text-hg-text-dim">
              <input
                type="checkbox"
                checked={selected.size > 0 && selected.size === people.length}
                onChange={selectAll}
                className="accent-hg-green"
                data-testid="people-select-all"
              />
              <span className="flex-1">Email / Name</span>
              <span className="w-24">Status</span>
              <span className="w-40 hidden md:block">Groups</span>
            </div>
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 size={18} className="animate-spin text-hg-green" /></div>
            ) : people.length === 0 ? (
              <div className="text-center py-16 text-sm text-hg-text-dim font-sans">No people match these filters.</div>
            ) : (
              people.map((p) => (
                <div key={p.id} className="px-4 py-2.5 border-b border-hg-border/40 last:border-b-0 flex items-center gap-3 text-xs font-sans hover:bg-hg-surface-hover/50">
                  <input
                    type="checkbox"
                    checked={selected.has(p.id)}
                    onChange={() => toggleSelect(p.id)}
                    className="accent-hg-green flex-shrink-0"
                    data-testid={`people-select-${p.id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-hg-text truncate">{p.email}</div>
                    {(() => {
                      const n = (p.name || '').trim();
                      const l = (p.last_name || '').trim();
                      const display = (l && n.toLowerCase().endsWith(l.toLowerCase())) ? n : [n, l].filter(Boolean).join(' ');
                      return display && <div className="text-[10px] text-hg-text-dim">{display}</div>;
                    })()}
                  </div>
                  <span className={`w-20 text-[9px] px-1.5 py-0.5 rounded-sm border uppercase tracking-wider ${STATUS_COLORS[p.status] || STATUS_COLORS.junk} text-center flex-shrink-0`}>
                    {p.status}
                  </span>
                  <div className="w-40 hidden md:flex flex-wrap gap-1 flex-shrink-0">
                    {(p.groups || []).slice(0, 2).map((g) => (
                      <span key={g.id} className="text-[9px] px-1.5 py-0.5 rounded-sm bg-hg-surface-hover text-hg-text-dim truncate max-w-full">{g.name}</span>
                    ))}
                    {(p.groups || []).length > 2 && <span className="text-[9px] text-hg-text-dim">+{p.groups.length - 2}</span>}
                  </div>
                </div>
              ))
            )}
            {/* Pagination — cursor based */}
            {(cursorStack.length > 0 || nextCursor) && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-hg-border text-[11px] font-sans">
                <button
                  data-testid="people-prev-page"
                  onClick={() => {
                    const stack = [...cursorStack];
                    const prev = stack.pop();
                    setCursorStack(stack);
                    setCursor(prev || null);
                  }}
                  disabled={cursorStack.length === 0}
                  className="px-3 py-1 border border-hg-border rounded-sm disabled:opacity-30"
                >Previous</button>
                <span className="text-hg-text-dim">
                  {people.length > 0 ? `Showing ${people.length} contacts` : ''}
                  {cursorStack.length > 0 && ` · page ${cursorStack.length + 1}`}
                </span>
                <button
                  data-testid="people-next-page"
                  onClick={() => {
                    if (nextCursor) {
                      setCursorStack([...cursorStack, cursor]);
                      setCursor(nextCursor);
                    }
                  }}
                  disabled={!nextCursor}
                  className="px-3 py-1 border border-hg-border rounded-sm disabled:opacity-30"
                >Next</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
